import { useQuery } from 'react-query'
import { useDebouncedValue } from './useDebouncedValue'
import { useEnvVars } from './useEnvVars'

/**
 * One place the customer can pick on the map step — phase 1 of the two-phase
 * Places flow, so it deliberately carries **no coordinates**. Turning a
 * suggestion into a point is `useAddressDetail`'s job.
 */
export type AddressSuggestion = {
  /**
   * Opaque id naming this place, and the only input phase 2 takes. Usually a
   * Google `place_id`; when Places is unavailable the backend answers from its
   * own Geocoding fallback with a self-encoded `geo:…` id that its detail route
   * resolves without any upstream call. Either way it is opaque here.
   */
  placeId: string
  /**
   * Primary line — the place's own name ("Burj Khalifa", "Quiqup Delivery LLC").
   * **Not unique:** Google legitimately returns the tower and the district of
   * the same name in one response, which is what `secondary` is for.
   */
  label: string
  /**
   * Secondary line that tells same-named places apart ("Sheikh Mohammed bin
   * Rashid Boulevard - Dubai - United Arab Emirates"). Absent on fallback
   * suggestions, which carry the whole address in `label`.
   */
  secondary?: string
}

/** Wire shape of one entry in `GET /v1/resolutions/link/address-suggest`. */
type SuggestionResponse = {
  place_id?: string
  label?: string
  secondary?: string
}

type AddressSuggestResponse = {
  suggestions?: SuggestionResponse[]
}

/**
 * Shortest query worth sending. Under three characters Places autocomplete has
 * nothing useful to go on, and the backend rejects it with a 400 anyway.
 */
export const MIN_QUERY_LENGTH = 3

/**
 * Longest query worth sending — the backend's own `q` cap. Beyond it the
 * request would 400, so we simply stop searching rather than burn a round trip.
 */
export const MAX_QUERY_LENGTH = 200

/**
 * Pause after the last keystroke before asking for suggestions.
 *
 * Places autocomplete *could* be driven per keystroke, and Google's session
 * token means a whole session bills as one lookup either way. This stays at
 * ~500ms regardless, because the request also crosses our own public,
 * rate-limited endpoint (~30/min per token): typing a full address one keystroke
 * at a time would spend that budget on answers the customer never reads, and a
 * throttled search shows nothing at all.
 */
export const SEARCH_DEBOUNCE_MS = 500

/**
 * The backend already caps at five; we cap again so a contract drift can't
 * flood a bottom-sheet dropdown on a phone.
 */
const MAX_SUGGESTIONS = 5

const NO_SUGGESTIONS: AddressSuggestion[] = []

/**
 * Mints a search-session token to thread through one suggest -> detail pair.
 *
 * Google bills an autocomplete *session* rather than each keystroke, but only
 * when the same token is sent with every suggest call and the detail call that
 * closes it — so the caller must hold one value for a whole search and mint a
 * fresh one once a pick has been detailed.
 *
 * A UUID when the platform offers one. The fallback is not merely defensive:
 * jsdom (the test environment) has no `crypto.randomUUID`, and neither do older
 * mobile Safari versions this public page has to serve. Any opaque string does —
 * Google only requires that it be stable across the pair, and the endpoint caps
 * it at 64 characters.
 */
export function newSearchSession(): string {
  const webCrypto = globalThis.crypto as
    | { randomUUID?: () => string }
    | undefined
  const uuid = webCrypto?.randomUUID?.()

  if (uuid) {
    return uuid
  }

  return `s-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
}

/**
 * Keeps only suggestions the UI can act on: an id to detail and a label to show.
 * Anything else is dropped rather than offered as an option that could not
 * resolve to a place.
 */
function toSuggestions(body: AddressSuggestResponse): AddressSuggestion[] {
  return (body.suggestions ?? [])
    .map((suggestion): AddressSuggestion | null => {
      const placeId = suggestion.place_id?.trim()
      const label = suggestion.label?.trim()

      if (!placeId || !label) {
        return null
      }

      return {
        placeId,
        label,
        secondary: suggestion.secondary?.trim() || undefined,
      }
    })
    .filter(
      (suggestion): suggestion is AddressSuggestion => suggestion !== null
    )
    .slice(0, MAX_SUGGESTIONS)
}

/**
 * Debounced address *suggestions* for the pin-confirm map step — phase 1 of the
 * two-phase Google Places flow (story sc-27850, Fix 2).
 *
 * Contract (geocoding service, geocoding-go PR #73):
 *   GET {GEOCODING_URL}/v1/resolutions/link/address-suggest
 *       ?token={token}&q={text}&session={session}
 *   -> 200 { suggestions: [ { place_id, label, secondary? } ] }   (max 5)
 *   -> 400 for a missing/invalid/expired token or a `q` outside 3..200 chars,
 *      429 when the public rate limit trips.
 *
 * This replaced a single-shot call to the old `address-search` endpoint, which
 * fronted Google's **Geocoding** API. Geocoding resolves address-like *strings*
 * and knows nothing about named places, so it answered "future tower" with a
 * helipad in Sharjah and "quiqup" with nothing at all. Places knows business and
 * building names, which is what a customer actually types. The old endpoint still
 * exists and still works — the backend keeps it as its own internal fallback, and
 * a fallback suggestion is indistinguishable here — so nothing calls it directly.
 *
 * Suggestions carry **no coordinates**; that is inherent to Places autocomplete,
 * not an omission. Use `useAddressDetail` on the picked `placeId` to get a point.
 *
 * An empty `suggestions` array means "no match" — a normal answer, not an error.
 * And like `usePinLinkContext`, **no failure is ever surfaced**: every error
 * resolves to an empty list, so a dead search still leaves the customer dragging
 * the pin and confirming by hand (`retry: false`, nothing here is worth waiting
 * on). In particular a 400 from an expired token stays silent here —
 * `confirm-link` remains the sole authority on link validity and the only call
 * that may show the "link expired" screen.
 *
 * Raw `fetch` with no request headers, keeping it a preflight-free simple GET;
 * see `usePinLinkContext` for the full reasoning. The Google key stays
 * server-side — nothing about it reaches this bundle, which is the whole reason
 * the Places proxy lives in geocoding-go rather than this public page calling
 * Google directly the way authenticated Quiqdash can.
 */
export function useAddressSearch(
  token: string | undefined,
  query: string,
  session?: string
) {
  const { env } = useEnvVars()
  const baseUrl = env.GEOCODING_URL

  // An empty query short-circuits the debounce instead of waiting it out:
  // debouncing exists to hold back the *next* request while the customer is
  // still typing, and "there is nothing to search" is not something to confirm
  // in 500ms time — the dropdown empties with the box.
  const trimmedQuery = query.trim()
  const debounced = useDebouncedValue(trimmedQuery, SEARCH_DEBOUNCE_MS)
  const debouncedQuery = trimmedQuery ? debounced : ''

  // Only ever fetch text the customer is still on. While the debounce lags
  // behind the box, `debouncedQuery` is text they have already moved past — and
  // firing for it is not merely wasteful: a session swap arriving in that
  // window (exactly what follows a resolved pick) makes a brand-new cache key
  // out of abandoned text, so the request goes out and opens a fresh Google
  // session that no detail call ever closes. Disabled rather than re-keyed, so
  // the previous key's cached suggestions stay on screen while they type.
  const enabled =
    !!baseUrl &&
    !!token &&
    debouncedQuery === trimmedQuery &&
    debouncedQuery.length >= MIN_QUERY_LENGTH &&
    debouncedQuery.length <= MAX_QUERY_LENGTH

  // `session` is part of the key, not just the URL: two sessions asking the same
  // text are genuinely different requests to Google, so an answer cached under a
  // spent session must not be served to a fresh one.
  const { data, isFetching } = useQuery<AddressSuggestion[]>(
    ['pin-link-address-suggest', token, session, debouncedQuery],
    async () => {
      const sessionParam = session
        ? `&session=${encodeURIComponent(session)}`
        : ''
      const url = `${baseUrl}/v1/resolutions/link/address-suggest?token=${encodeURIComponent(
        token as string
      )}&q=${encodeURIComponent(debouncedQuery)}${sessionParam}`

      let response: Response
      try {
        // No headers: keep it a simple (preflight-free) cross-origin GET.
        response = await fetch(url)
      } catch {
        return NO_SUGGESTIONS
      }

      if (!response.ok) {
        return NO_SUGGESTIONS
      }

      try {
        const body = (await response.json()) as AddressSuggestResponse
        return toSuggestions(body ?? {})
      } catch {
        return NO_SUGGESTIONS
      }
    },
    {
      enabled,
      // The same text in the same session always resolves to the same places;
      // don't re-ask when the customer edits back to something already searched.
      staleTime: Infinity,
      retry: false,
    }
  )

  return {
    suggestions: data ?? NO_SUGGESTIONS,
    /**
     * True while a search the customer can see the result of is in flight.
     * Gated on `enabled` so a too-short query never reads as "searching".
     */
    isSearching: enabled && isFetching,
    /**
     * True once the debounce has settled on a searchable query — i.e. the
     * customer has typed enough for suggestions to be expected. Lets the input
     * tell "keep typing" apart from "we looked and found nothing".
     */
    hasSearchableQuery: enabled,
  }
}
